import { useCallback, useEffect, useState, useMemo } from "react";
import { PageHeader } from "@/components/AppLayout";
import { api } from "@/lib/api";
import { Plus, Search, X, Upload, Pencil, Trash2 } from "lucide-react";
import ImportStudentsModal from "@/components/ImportStudentsModal";
import { formatApiError } from "@/lib/formatError";

const BELT_ORDER = [
  "white",
  "gray-white", "gray", "gray-black",
  "yellow-white", "yellow", "yellow-black",
  "orange-white", "orange", "orange-black",
  "green-white", "green", "green-black",
  "blue", "purple", "brown", "black",
];
const BELT_CLASS = Object.fromEntries(BELT_ORDER.map(b => [b, `belt-${b}`]));

const BLANK = { name: "", email: "", guardian_email: "", phone: "", notes: "", belt: "white", stripes: 0, status: "trial", months: 0, membership_plan: "Unlimited", membership_price: 120, membership_renews: "" };

// Dual-purpose modal: creates a new student when `editing` is null,
// otherwise PATCHes the given student. Same fields either way.
function StudentFormModal({ editing, onClose, onSaved }) {
  const isEdit = !!editing;
  const [form, setForm] = useState(() => isEdit
    ? { ...BLANK, ...editing, membership_renews: editing.membership_renews || "" }
    : { ...BLANK });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [planOptions, setPlanOptions] = useState(["Adult Unlimited", "Adult 3x/week", "Kids Unlimited", "Kids 2x/week", "Family", "Trial", "Drop-in", "Custom"]);
  const [academyPresets, setAcademyPresets] = useState([]);
  const [customMode, setCustomMode] = useState(true);
  useEffect(() => {
    api.get("/billing/plans").then(({ data }) => {
      setPlanOptions(data.all || planOptions);
      const presets = data.academy_presets || [];
      setAcademyPresets(presets);
      // Default to the matching preset dropdown unless the current plan isn't one of them.
      if (presets.length > 0) {
        setCustomMode(!presets.some(p => p.name === form.membership_plan));
      }
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const setNum = (k) => (e) => setForm({ ...form, [k]: Number(e.target.value) });

  const pickPreset = (e) => {
    const v = e.target.value;
    if (v === "__custom__") { setCustomMode(true); return; }
    const preset = academyPresets.find(p => p.name === v);
    setCustomMode(false);
    setForm({ ...form, membership_plan: v, membership_price: preset ? preset.price : form.membership_price });
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true); setError("");
    try {
      if (isEdit) {
        // Send only mutable fields (exclude id/initials/counts).
        const { student_id, initials, classes_count, streak, monthly_avg,
                promoted_at, promoted_by, last_seen, created_at, academy_id,
                membership_renews, ...rest } = form;
        const body = { ...rest };
        // Empty renews → null so backend stores None.
        if (membership_renews) body.membership_renews = membership_renews;
        const { data } = await api.patch(`/students/${editing.student_id}`, body);
        onSaved(data);
      } else {
        const { data } = await api.post("/students", form);
        onSaved(data);
      }
    } catch (err) { setError(formatApiError(err, "Save failed")); }
    setSaving(false);
  };

  return (
    <div style={{position:"fixed", inset:0, background:"rgba(0,0,0,0.75)", display:"grid", placeItems:"center", zIndex:50, padding:16}} onClick={onClose}>
      <form onSubmit={submit} onClick={e=>e.stopPropagation()} className="card p-6 w-[480px] max-w-[92vw] max-h-[92vh] overflow-y-auto" data-testid={isEdit ? "edit-student-modal" : "add-student-modal"}>
        <div className="flex items-center justify-between mb-5">
          <div>
            <div className="label" style={{color:"var(--green)"}}>{isEdit ? "Edit student" : "New student"}</div>
            <div className="text-[20px] font-bold uppercase mt-1 tracking-tight">{isEdit ? form.name || "Update details" : "Add to roster"}</div>
          </div>
          <button type="button" onClick={onClose} data-testid="student-form-close"><X size={18}/></button>
        </div>
        <div className="space-y-3">
          <label className="block label">Name<input className="input mt-1" required value={form.name} onChange={set("name")} data-testid="sf-name"/></label>
          <label className="block label">Email<input className="input mt-1" type="email" value={form.email || ""} onChange={set("email")} data-testid="sf-email"/></label>
          <label className="block label">Parent / Guardian email <span className="label-tiny normal-case" style={{color:"var(--text-dim)"}}>(kids only — enables family switcher)</span>
            <input className="input mt-1" type="email" placeholder="parent@example.com" value={form.guardian_email || ""} onChange={set("guardian_email")} data-testid="sf-guardian-email"/>
          </label>
          <label className="block label">Phone<input className="input mt-1" type="tel" placeholder="+507-6000-0000" value={form.phone || ""} onChange={set("phone")} data-testid="sf-phone"/></label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block label">Belt
              <select className="input mt-1" value={form.belt} onChange={set("belt")} data-testid="sf-belt">
                {BELT_ORDER.map(b=><option key={b} value={b}>{b}</option>)}
              </select>
            </label>
            <label className="block label">Stripes<input type="number" min="0" max="4" className="input mt-1" value={form.stripes} onChange={setNum("stripes")} data-testid="sf-stripes"/></label>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <label className="block label">Status
              <select className="input mt-1" value={form.status} onChange={set("status")} data-testid="sf-status">
                <option value="active">Active</option><option value="trial">Trial</option><option value="lapsed">Lapsed</option>
              </select>
            </label>
            <label className="block label">Months training<input type="number" min="0" className="input mt-1" value={form.months} onChange={setNum("months")} data-testid="sf-months"/></label>
          </div>
          {academyPresets.length > 0 && (
            <label className="block label">Discipline / Membership
              <select className="input mt-1" value={customMode ? "__custom__" : form.membership_plan} onChange={pickPreset} data-testid="sf-plan-preset">
                {academyPresets.map(p => <option key={p.name} value={p.name}>{p.name} — ${p.price}/mo</option>)}
                <option value="__custom__">Custom / other…</option>
              </select>
            </label>
          )}
          <div className="grid grid-cols-2 gap-3">
            {(academyPresets.length === 0 || customMode) && (
              <label className="block label">{academyPresets.length > 0 ? "Custom plan name" : "Membership plan"}
                <input list="plan-presets" className="input mt-1" value={form.membership_plan || ""}
                       onChange={set("membership_plan")} data-testid="sf-plan"
                       placeholder="e.g. Adult Unlimited" />
                <datalist id="plan-presets">
                  {planOptions.map(p => <option key={p} value={p} />)}
                </datalist>
              </label>
            )}
            <label className="block label">Price ($/mo)<input type="number" min="0" className="input mt-1" value={form.membership_price || 0} onChange={setNum("membership_price")} data-testid="sf-price"/></label>
          </div>
          <label className="block label">Notes <span className="label-tiny normal-case" style={{color:"var(--text-dim)"}}>(injuries, allergies, emergency contact)</span>
            <textarea className="input mt-1" rows="3" value={form.notes || ""} onChange={set("notes")} data-testid="sf-notes"/>
          </label>
        </div>
        {error && <div className="label-tiny mt-3" style={{color:"var(--danger)"}} data-testid="sf-error">{error}</div>}
        <button type="submit" disabled={saving} className="btn btn-primary w-full mt-5" data-testid="sf-submit">
          {saving ? "Saving…" : (isEdit ? "Save changes" : "Add student")}
        </button>
      </form>
    </div>
  );
}

export default function Students() {
  const [students, setStudents] = useState([]);
  const [selected, setSelected] = useState(null);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [showForm, setShowForm] = useState(null); // null | { editing: student|null }
  const [showImport, setShowImport] = useState(false);
  const [annualFee, setAnnualFee] = useState(null);
  const [chargingFee, setChargingFee] = useState(false);

  const load = async () => {
    const r = await api.get("/students");
    setStudents(r.data);
    if (selected) {
      const fresh = r.data.find(s => s.student_id === selected.student_id);
      setSelected(fresh || r.data[0] || null);
    } else {
      setSelected(r.data[0] || null);
    }
  };
  useEffect(() => {
    load();
    api.get("/billing/plans").then(({ data }) => setAnnualFee(data.annual_fee || null)).catch(() => {});
  }, []);

  const onSaved = (student) => {
    setShowForm(null);
    load().then(() => setSelected(student));
  };

  const remove = async () => {
    if (!selected) return;
    if (!window.confirm(`Remove ${selected.name} from the roster? This also deletes their attendance history.`)) return;
    await api.delete(`/students/${selected.student_id}`);
    setSelected(null);
    load();
  };

  const chargeAnnualFee = async () => {
    if (!selected || !annualFee) return;
    if (!window.confirm(`Record a $${annualFee.amount} "${annualFee.label}" payment for ${selected.name}? This does not extend their monthly renewal.`)) return;
    setChargingFee(true);
    try {
      await api.post("/billing/record-fee", { student_id: selected.student_id, amount: annualFee.amount, note: annualFee.label });
      load();
    } catch (err) {
      alert(formatApiError(err, "Could not record fee"));
    }
    setChargingFee(false);
  };

  const filtered = useMemo(() => students.filter(s =>
    (filter === "all" || s.status === filter) &&
    (s.name.toLowerCase().includes(search.toLowerCase()) || (s.email || "").toLowerCase().includes(search.toLowerCase()))
  ), [students, search, filter]);

  const counts = useMemo(() => ({
    all: students.length,
    active: students.filter(s=>s.status==="active").length,
    trial: students.filter(s=>s.status==="trial").length,
    lapsed: students.filter(s=>s.status==="lapsed").length,
  }), [students]);

  const promote = async () => { if (!selected) return; const { data } = await api.post(`/students/${selected.student_id}/promote`); setSelected(data); load(); };

  return (
    <div data-testid="page-students">
      <PageHeader section="Roster" title="Students"
        right={<div className="flex gap-2">
          <button className="btn btn-outline" onClick={()=>setShowImport(true)} data-testid="btn-import-csv"><Upload size={14}/>Import CSV</button>
          <button className="btn btn-primary" onClick={()=>setShowForm({ editing: null })} data-testid="btn-add-student"><Plus size={14}/>Add student</button>
        </div>}
      />
      <div className="p-8">
        <div className="flex items-center justify-between mb-5">
          <div className="relative max-w-[420px] flex-1">
            <Search size={15} style={{ position:"absolute", left:14, top:13, color:"var(--text-muted)" }}/>
            <input className="input pl-9" placeholder="Search by name, belt, email…" value={search} onChange={e=>setSearch(e.target.value)} data-testid="students-search"/>
          </div>
          <div className="flex gap-2">
            {[["all","All"],["active","Active"],["trial","Trial"],["lapsed","Lapsed"]].map(([k,l]) => (
              <button key={k} onClick={()=>setFilter(k)} className={`pill ${filter===k ? "pill-active" : ""}`} style={filter!==k?{background:"var(--bg-card)",color:"var(--text-dim)",border:"1px solid var(--border)"}:null} data-testid={`filter-${k}`}>
                {l} · {counts[k]}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 card overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr style={{ borderBottom: "1px solid var(--border)" }}>
                  <th className="label-tiny text-left p-4">Athlete</th>
                  <th className="label-tiny text-left p-4">Belt</th>
                  <th className="label-tiny text-left p-4">Status</th>
                  <th className="label-tiny text-left p-4">Last seen</th>
                  <th className="label-tiny text-right p-4">Months</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(s => (
                  <tr key={s.student_id} onClick={()=>setSelected(s)} className="cursor-pointer hover:bg-[var(--bg-card-hover)]" style={{ borderBottom: "1px solid var(--border)", background: selected?.student_id===s.student_id ? "var(--bg-card-hover)" : "transparent" }} data-testid={`student-row-${s.student_id}`}>
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full flex items-center justify-center text-[11px] font-semibold" style={{ background:"var(--bg-elev)", border:"1px solid var(--border)" }}>{s.initials}</div>
                        <span className="font-semibold uppercase tracking-tight">{s.name}</span>
                      </div>
                    </td>
                    <td className="p-4"><div className="belt-chip"><span className={`belt-bar ${BELT_CLASS[s.belt]}`}/><span className="label-tiny capitalize">{s.belt} · {s.stripes}</span></div></td>
                    <td className="p-4"><span className={`pill pill-${s.status}`}>{s.status}</span></td>
                    <td className="p-4" style={{color:"var(--text-dim)"}}>{s.last_seen}</td>
                    <td className="p-4 text-right mono">{s.months}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {selected && (
            <div className="card p-6" data-testid="student-detail">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center text-[14px] font-semibold shrink-0" style={{ background:"var(--bg-elev)", border:"1px solid var(--border)" }}>{selected.initials}</div>
                  <div className="min-w-0">
                    <div className="font-bold text-[18px] uppercase tracking-tight truncate">{selected.name}</div>
                    <div className="label-tiny mt-0.5">Member · {selected.months} months</div>
                  </div>
                </div>
                <div className="flex gap-1 shrink-0">
                  <button className="btn btn-outline" style={{padding:"6px 8px"}} onClick={()=>setShowForm({ editing: selected })} title="Edit" data-testid="btn-edit-student"><Pencil size={14}/></button>
                  <button className="btn btn-outline" style={{padding:"6px 8px", color:"var(--danger)"}} onClick={remove} title="Remove from roster" data-testid="btn-delete-student"><Trash2 size={14}/></button>
                </div>
              </div>
              {(selected.email || selected.phone || selected.guardian_email) && (
                <div className="card p-3 mt-4 space-y-1" data-testid="student-contact">
                  {selected.email && <div className="label-tiny break-all"><span style={{color:"var(--text-dim)"}}>Email · </span>{selected.email}</div>}
                  {selected.guardian_email && <div className="label-tiny break-all"><span style={{color:"var(--text-dim)"}}>Guardian · </span>{selected.guardian_email}</div>}
                  {selected.phone && <div className="label-tiny mono"><span style={{color:"var(--text-dim)", fontFamily:"inherit"}}>Phone · </span>{selected.phone}</div>}
                </div>
              )}
              <div className="grid grid-cols-3 gap-3 mt-5">
                {[["Classes", selected.classes_count],["Streak", `${selected.streak}d`],["Mo. avg", selected.monthly_avg]].map(([l,v]) => (
                  <div key={l} className="card p-3 text-center"><div className="label-tiny">{l}</div><div className="text-[20px] font-bold mt-1">{v}</div></div>
                ))}
              </div>
              <div className="label mt-5">Current rank</div>
              <div className="card p-3 mt-2 flex items-center gap-3"><span className={`belt-bar ${BELT_CLASS[selected.belt]}`} style={{width:48,height:14}}/><span className="font-semibold uppercase tracking-tight">{selected.belt} · {selected.stripes} stripes</span></div>
              {selected.promoted_at && <div className="label-tiny mt-2">Promoted {selected.promoted_at} by {selected.promoted_by}</div>}
              <div className="label mt-5">Membership</div>
              <div className="card p-3 mt-2 flex items-center justify-between">
                <div><div className="font-semibold uppercase tracking-tight">{selected.membership_plan} · ${selected.membership_price}/mo</div>{selected.membership_renews && <div className="label-tiny mt-1">Renews {selected.membership_renews}</div>}</div>
                <span className={`pill pill-${selected.status}`}>{selected.status}</span>
              </div>
              <div className="mt-5">
                <button className="btn btn-primary w-full" onClick={promote} data-testid="btn-promote">Promote</button>
              </div>
              {annualFee && (
                <div className="mt-3">
                  <button className="btn btn-outline w-full" disabled={chargingFee} onClick={chargeAnnualFee} data-testid="btn-charge-annual-fee">
                    {chargingFee ? "Recording…" : `Charge ${annualFee.label} ($${annualFee.amount})`}
                  </button>
                </div>
              )}
              {selected.notes && (
                <div className="mt-5" data-testid="student-notes">
                  <div className="label">Notes</div>
                  <div className="card p-3 mt-2 label-tiny leading-relaxed whitespace-pre-wrap" style={{color:"var(--text)"}}>{selected.notes}</div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      {showForm && <StudentFormModal editing={showForm.editing} onClose={()=>setShowForm(null)} onSaved={onSaved}/>}
      {showImport && <ImportStudentsModal onClose={()=>setShowImport(false)} onDone={load}/>}
    </div>
  );
}
